import React, { useEffect, useRef } from 'react';
import formStyles from '../../styles/SmartSendForm.module.css';

/**
 * ConfirmAddressChangeModal - Modal for confirming address change
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {Function} onConfirm - Callback when user confirms
 * @param {Function} onCancel - Callback when user cancels
 */
const ConfirmAddressChangeModal = ({ isOpen, onConfirm, onCancel }) => {
  const modalContentRef = useRef(null);
  const cancelButtonRef = useRef(null);
  const continueButtonRef = useRef(null);
  const previousActiveElementRef = useRef(null);

  // Focus management: trap focus within modal and handle Escape key
  useEffect(() => {
    if (!isOpen) return;

    // Store the element that had focus before modal opened
    previousActiveElementRef.current = document.activeElement;

    // Focus the first button (Cancel) when modal opens
    if (cancelButtonRef.current) {
      cancelButtonRef.current.focus();
    }

    // Get all focusable elements within the modal
    const getFocusableElements = () => {
      if (!modalContentRef.current) return [];
      const focusableSelectors = [
        'button:not([disabled])',
        '[href]',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(', ');
      return Array.from(
        modalContentRef.current.querySelectorAll(focusableSelectors)
      );
    };

    // Handle Escape key to close modal
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };

    // Trap focus within modal
    const handleTab = (e) => {
      if (e.key !== 'Tab') return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) return;

      // Only trap if focus is currently within the modal
      if (
        !modalContentRef.current ||
        !modalContentRef.current.contains(document.activeElement)
      ) {
        // If focus escaped, bring it back to first element
        e.preventDefault();
        focusableElements[0].focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      // If Shift+Tab on first element, move to last element
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
        return;
      }

      // If Tab on last element, move to first element
      if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
        return;
      }
    };

    // Add event listeners
    document.addEventListener('keydown', handleEscape);
    document.addEventListener('keydown', handleTab);

    // Cleanup: restore focus to previous element when modal closes
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('keydown', handleTab);

      // Restore focus to the element that had focus before modal opened
      if (previousActiveElementRef.current) {
        const previousElement = previousActiveElementRef.current;
        // Check if element is still in the DOM and has focus method
        if (
          document.body.contains(previousElement) &&
          typeof previousElement.focus === 'function'
        ) {
          // Use setTimeout to ensure modal is fully removed from DOM first
          setTimeout(() => {
            previousElement.focus();
          }, 0);
        }
      }
    };
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  return (
    <div className={formStyles.modalOverlay} onClick={onCancel}>
      <div
        ref={modalContentRef}
        className={formStyles.modalContent}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-address-change-modal-title"
        aria-describedby="confirm-address-change-modal-message"
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          className={formStyles.modalTitle}
          id="confirm-address-change-modal-title"
        >
          Edit Delivery Address?
        </h3>
        <p
          className={formStyles.modalMessage}
          id="confirm-address-change-modal-message"
        >
          Editing the delivery address will reset your selected delivery date. Continue?
        </p>
        <div className={formStyles.modalActions}>
          <button
            ref={cancelButtonRef}
            type="button"
            className={`${formStyles.modalButton} ${formStyles.modalButtonSecondary}`}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            ref={continueButtonRef}
            type="button"
            className={`${formStyles.modalButton} ${formStyles.modalButtonPrimary}`}
            onClick={onConfirm}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmAddressChangeModal;

